import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const PointBuyCalculator5e = () => {
  const [abilityScores, setAbilityScores] = useState({
    strength: 8,
    dexterity: 8,
    constitution: 8,
    intelligence: 8,
    wisdom: 8,
    charisma: 8
  });

  const [racialBonuses, setRacialBonuses] = useState({
    strength: 0,
    dexterity: 0,
    constitution: 0,
    intelligence: 0,
    wisdom: 0,
    charisma: 0
  });

  const [totalPoints, setTotalPoints] = useState(27);
  const [error, setError] = useState('');
  const [showMore, setShowMore] = useState(false);

  const pointCosts = {
    8: 0,
    9: 1,
    10: 2,
    11: 3,
    12: 4,
    13: 5,
    14: 7,
    15: 9
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const score = parseInt(value, 10);
    if (score < 8 || score > 15) {
      setError('Scores must be between 8 and 15');
      return;
    }

    const newAbilityScores = { ...abilityScores, [name]: score };
    const newTotalPoints = totalPoints - pointCosts[abilityScores[name]] + pointCosts[score];

    if (newTotalPoints < 0) {
      setError('You have exceeded the available points');
      return;
    }

    setAbilityScores(newAbilityScores);
    setTotalPoints(newTotalPoints);
    setError('');
  };

  const applyRacialBonuses = () => {
    const newAbilityScores = {
      strength: abilityScores.strength + racialBonuses.strength,
      dexterity: abilityScores.dexterity + racialBonuses.dexterity,
      constitution: abilityScores.constitution + racialBonuses.constitution,
      intelligence: abilityScores.intelligence + racialBonuses.intelligence,
      wisdom: abilityScores.wisdom + racialBonuses.wisdom,
      charisma: abilityScores.charisma + racialBonuses.charisma
    };

    return newAbilityScores;
  };

  const finalScores = applyRacialBonuses();

  const chartData = {
    labels: ['Strength', 'Dexterity', 'Constitution', 'Intelligence', 'Wisdom', 'Charisma'],
    datasets: [
      {
        label: 'Ability Scores',
        data: Object.values(finalScores),
        backgroundColor: [
          'rgba(75, 192, 192, 0.5)',
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 206, 86, 0.5)',
          'rgba(153, 102, 255, 0.5)',
          'rgba(255, 159, 64, 0.5)',
          'rgba(255, 99, 132, 0.5)'
        ],
        borderColor: [
          'rgba(75, 192, 192, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(255, 99, 132, 1)'
        ],
        borderWidth: 1
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <Helmet>
        <title>Point Buy Calculator 5e | Dungeons & Dragons Character Creation</title>
        <meta name="description" content="Create balanced D&D 5e characters with our Point Buy Calculator. Allocate points to ability scores and apply racial bonuses." />
        <meta name="keywords" content="D&D 5e, Point Buy Calculator, ability scores, racial bonuses, character creation" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center mb-8">
            <div className="text-4xl text-blue-600 mr-4">D&D</div>
            <h1 className="text-3xl font-bold text-gray-800">Point Buy Calculator 5e</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>The Point Buy Calculator helps you allocate points to your ability scores and apply racial bonuses to create balanced D&D 5e characters.</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-lg shadow-lg p-6"
          >
            <div className="grid grid-cols-1 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Strength:</label>
                <input
                  type="number"
                  id="strength"
                  name="strength"
                  value={abilityScores.strength}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter strength score"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dexterity:</label>
                <input
                  type="number"
                  id="dexterity"
                  name="dexterity"
                  value={abilityScores.dexterity}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter dexterity score"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Constitution:</label>
                <input
                  type="number"
                  id="constitution"
                  name="constitution"
                  value={abilityScores.constitution}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter constitution score"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Intelligence:</label>
                <input
                  type="number"
                  id="intelligence"
                  name="intelligence"
                  value={abilityScores.intelligence}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter intelligence score"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Wisdom:</label>
                <input
                  type="number"
                  id="wisdom"
                  name="wisdom"
                  value={abilityScores.wisdom}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter wisdom score"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Charisma:</label>
                <input
                  type="number"
                  id="charisma"
                  name="charisma"
                  value={abilityScores.charisma}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter charisma score"
                />
              </div>
            </div>

            {error && (
              <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
                {error}
              </div>
            )}

            <button
              onClick={() => setShowMore(!showMore)}
              className="mt-8 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {showMore ? 'Hide More' : 'Show More'}
            </button>

            {showMore && (
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Final Ability Scores</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Strength</h3>
                    <p className="text-gray-600">{finalScores.strength}</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Dexterity</h3>
                    <p className="text-gray-600">{finalScores.dexterity}</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Constitution</h3>
                    <p className="text-gray-600">{finalScores.constitution}</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Intelligence</h3>
                    <p className="text-gray-600">{finalScores.intelligence}</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Wisdom</h3>
                    <p className="text-gray-600">{finalScores.wisdom}</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Charisma</h3>
                    <p className="text-gray-600">{finalScores.charisma}</p>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Score'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Ability'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Ability Scores Breakdown',
                          font: {
                            size: 16
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </motion.div>
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use</h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Point Buy Calculator:</p>
            <ol className="list-decimal pl-6 mt-2">
              <li>Enter your desired ability scores for each attribute (Strength, Dexterity, Constitution, Intelligence, Wisdom, Charisma).</li>
              <li>Ensure that the total points used do not exceed the available points (27 points by default).</li>
              <li>Apply racial bonuses to your ability scores to get the final scores.</li>
              <li>Use the "Show More" button to view the final ability scores and a graphical representation.</li>
            </ol>
          </div>
        </div>

        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ</h2>
          <div className="text-gray-600">
            <p><strong>Q: What is the point buy system?</strong></p>
            <p>A: The point buy system is a method in D&D 5e for assigning ability scores to your character. Each score has a point cost, and you have a limited number of points to spend.</p>

            <p><strong>Q: How do I apply racial bonuses?</strong></p>
            <p>A: After allocating your points, add the racial bonuses to the corresponding ability scores to get the final scores.</p>

            <p><strong>Q: Can I exceed the total points available?</strong></p>
            <p>A: No, you must stay within the total points available (27 points by default). Exceeding this limit will result in an error.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PointBuyCalculator5e;